import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { JobsPanelState, JobsStatus, jobStatusToString, JobsType } from "../../../constants/Jobs";
import { FeatureTableContext, JobContext } from "../../../Context";
import { hideDocumentationPoints } from "../../../utils/jobsUtils";
import "./JobDetails.css";

import BackArrowIcon from "../../../assets/arrow-back.svg?react";
import CloseIcon from "../../../assets/close.svg?react";
import StatusIcon from "../../../assets/jobs/status.svg?react";

import { RightPanelTabs } from "../../../constants/Panels";
import { setRightPanelActiveTab } from "../../../store/appSlice";
import JobPlanDetails from "./JobPlanDetails";
import JobQA1Details from "./JobQA1Details";
import JobPlanRejectedDetails from "./JobPlanRejectedDetails";
import JobQA2Details from "./JobQA2Details";
import JobReadOnlyDetails from "./JobReadOnlyDetails";
import JobFailedSksdSending from "./JobFailedSksdSending";
import JobAnalysisCompleted from "./JobAnalysisCompleted";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";

interface JobDetailsProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobDetails: React.FC<JobDetailsProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const featureTableContext = useContext(FeatureTableContext);

    const jobsContext = useContext(JobContext);

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const getStatus = () => {
        const status = jobObject.attributes.status;
        return jobStatusToString(status);
    };

    const closePanel = () => {
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
        returnToJobList();
    };

    useEffect(() => {
        return () => {
            featureTableContext?.activeLayer.set(undefined);
            featureTableContext?.isOpen.set(false);
            hideDocumentationPoints();
        };
    }, []);

    return (
        <div className="job-details-container">
            <div className="job-details-action-header flex-row space-between">
                <button className="empty-button center small-gap" onClick={returnToJobList}>
                    <BackArrowIcon />
                    <span>Tilbake</span>
                </button>
                <button type="button" className="empty-button" onClick={closePanel}>
                    <CloseIcon />
                </button>
            </div>
            <div className="job-details-header">
                <div className="w-100">
                    <div className="job-card-status-chip">
                        <StatusIcon />
                        <span>{getStatus()}</span>
                        <span style={{ width: "24px" }}></span>
                    </div>
                </div>
            </div>
            {jobObject.attributes.status === JobsStatus.Plan && (
                <JobPlanDetails
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {jobObject.attributes.status === JobsStatus.QA1 && (
                <JobQA1Details
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {jobObject.attributes.status === JobsStatus.PlanIkkeGodkjent && (
                <JobPlanRejectedDetails
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {jobObject.attributes.status === JobsStatus.QA2 && (
                <JobQA2Details
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {jobObject.attributes.status === JobsStatus.FailedSendingToSksd && (
                <JobFailedSksdSending
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {((jobObject.attributes.status === JobsStatus.Ferdig &&
                jobObject.attributes.jobb_type === JobsType.DRONE_CONTROL) ||
                jobObject.attributes.status === JobsStatus.KontrollskjermingAvvik) && (
                <JobAnalysisCompleted
                    jobObject={jobObject}
                    setJobPanelState={setJobPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {jobObject.attributes.status !== JobsStatus.Plan &&
                jobObject.attributes.status !== JobsStatus.QA1 &&
                jobObject.attributes.status !== JobsStatus.PlanIkkeGodkjent &&
                jobObject.attributes.status !== JobsStatus.QA2 &&
                jobObject.attributes.status !== JobsStatus.FailedSendingToSksd &&
                jobObject.attributes.status !== JobsStatus.KontrollskjermingAvvik &&
                !(
                    jobObject.attributes.status === JobsStatus.Ferdig &&
                    jobObject.attributes.jobb_type === JobsType.DRONE_CONTROL
                ) && (
                    <JobReadOnlyDetails
                        jobObject={jobObject}
                        storeHighlightHandle={storeHighlightHandle}
                        clearHighlightedFeatures={clearHighlightedFeatures}
                    />
                )}
        </div>
    );
};

export default JobDetails;
